import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { ARTICLES, JOURNAL_CATEGORIES } from "@/data/content";

const imgCls =
  "h-full w-full object-cover opacity-90 saturate-[0.85] contrast-[1.05] transition-[transform,opacity] duration-700 group-hover:scale-[1.04] group-hover:opacity-100";

const FeaturedDispatch = ({ article }) => (
  <Reveal>
    <Link
      to={`/journal/${article.slug}`}
      data-testid={`journal-featured-${article.slug}`}
      className="group grid overflow-hidden border border-[#282D37] bg-[#121418] transition-[border-color] duration-500 hover:border-[#E52E2E]/60 lg:grid-cols-[1.5fr_1fr]"
    >
      <div className="relative overflow-hidden">
        <img src={article.image} alt={article.title} className={`${imgCls} aspect-[16/9] lg:aspect-auto lg:absolute lg:inset-0`} />
        <div className="absolute left-5 top-5 flex items-center gap-2.5 bg-[#0A0B0D]/80 px-3 py-1.5 backdrop-blur-sm">
          <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
          <span className="font-mono text-[10px] font-bold tracking-[0.3em] text-white">LATEST DISPATCH</span>
        </div>
      </div>
      <div className="flex flex-col justify-center p-8 sm:p-12">
        <div className="flex items-center gap-4 font-mono text-[10px] tracking-[0.3em]">
          <span className="text-[#E52E2E]">{article.category}</span>
          <span className="text-[#5F6573]">{article.date}</span>
          <span className="text-[#5F6573]">{article.readTime}</span>
        </div>
        <h2 className="mt-5 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E] sm:text-4xl lg:text-5xl">
          {article.title}
        </h2>
        <p className="mt-5 text-sm leading-relaxed text-[#9EA4B0] sm:text-base">{article.summary}</p>
        <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
          READ DISPATCH
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  </Reveal>
);

const DispatchRow = ({ article, index }) => (
  <Reveal delay={0.05 * index}>
    <Link
      to={`/journal/${article.slug}`}
      data-testid={`journal-card-${article.slug}`}
      className="group relative block border-b border-[#282D37] transition-colors duration-500 hover:bg-[#121418]"
    >
      <div className="flex items-center gap-6 px-4 py-8 sm:gap-10 sm:px-8 lg:py-10">
        <span className="hidden font-mono text-sm tracking-[0.3em] text-[#3A404E] transition-colors duration-500 group-hover:text-[#E52E2E] sm:block">
          {String(index + 2).padStart(2, "0")}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] tracking-[0.3em]">
            <span className="text-[#E52E2E]">{article.category}</span>
            <span className="text-[#5F6573]">{article.date}</span>
            <span className="text-[#5F6573]">{article.readTime}</span>
          </div>
          <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight tracking-wide text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E] sm:text-3xl lg:text-4xl">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 max-w-2xl text-sm leading-relaxed text-[#9EA4B0]">{article.summary}</p>
        </div>
        <div className="relative hidden h-24 w-40 shrink-0 overflow-hidden border border-[#282D37] md:block">
          <img src={article.image} alt="" className={imgCls} />
        </div>
        <ArrowUpRight
          size={22}
          className="shrink-0 text-[#3A404E] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#E52E2E]"
        />
      </div>
      <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
    </Link>
  </Reveal>
);

const Journal = () => {
  const [active, setActive] = useState("ALL");
  const filtered = active === "ALL" ? ARTICLES : ARTICLES.filter((a) => a.category === active);
  const [featured, ...rest] = filtered;

  return (
    <div className="relative pt-32">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[480px]"
        style={{
          background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
        }}
      />
      <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FORGE JOURNAL // DISPATCHES</p>
              <h1 className="mt-5 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
                The Journal
              </h1>
            </div>
            <p className="max-w-sm pb-2 text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
              Studio logs, world notes and development dispatches. Depth over frequency — we speak
              when there is something worth saying.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-[#282D37] py-5" data-testid="journal-filters">
            {JOURNAL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`group relative font-mono text-[11px] font-bold tracking-[0.25em] transition-colors duration-300 ${
                  active === cat ? "text-[#E52E2E]" : "text-[#9EA4B0] hover:text-[#F4F5F7]"
                }`}
              >
                {cat}
                <span
                  className={`absolute -bottom-[21px] left-0 h-px bg-[#E52E2E] transition-[width] duration-300 ${
                    active === cat ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </button>
            ))}
            <span className="ml-auto hidden font-mono text-[10px] tracking-[0.3em] text-[#5F6573] sm:block">
              {filtered.length} DISPATCH{filtered.length === 1 ? "" : "ES"}
            </span>
          </div>
        </Reveal>
      </header>

      <section className="relative mx-auto max-w-[1400px] px-6 py-14 lg:px-10">
        {filtered.length === 0 ? (
          <p data-testid="journal-empty" className="border border-dashed border-[#282D37] p-16 text-center font-mono text-xs tracking-[0.3em] text-[#5F6573]">
            NO DISPATCHES IN THIS CHANNEL — YET.
          </p>
        ) : (
          <>
            {featured && <FeaturedDispatch article={featured} />}
            {rest.length > 0 && (
              <motion.div layout className="mt-16 border-t border-[#282D37]">
                {rest.map((a, i) => (
                  <DispatchRow key={a.slug} article={a} index={i} />
                ))}
              </motion.div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default Journal;
